import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout, Menu, Result, Avatar, Dropdown, Space, Typography, Modal, Form, Input, message } from 'antd';
import {
  CommentOutlined,
  RobotOutlined,
  TeamOutlined,
  SendOutlined,
  ShareAltOutlined,
  RiseOutlined,
  TagsOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  MessageOutlined,
  GiftOutlined,
  UserOutlined,
  SafetyOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  LockOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { changePassword } from '../services/authService';
import PostComment from './PostComment';
import BotManagement from './BotManagement';
import GroupManagement from './GroupManagement';
import ContentDistribution from './ContentDistribution';
import SocialNetwork from './SocialNetwork';
import AccountNurture from './AccountNurture';
import TagManagement from './TagManagement';
import TalkBoost from './TalkBoost';
import LiveRoomActiveAtmosphere from './LiveRoomActiveAtmosphere';
import LiveChat from './LiveChat';
import GiftSend from './GiftSend';
import AccountManagement from './AccountManagement';
import RoleManagement from './RoleManagement';
import OperationLog from './OperationLog';
import BotEmployee from './BotEmployee';
import BotGroupManagement from './BotGroupManagement';
import EmployeeChat from './EmployeeChat';
import Polymarket from './Polymarket';
import BotAvatarAssign from './BotAvatarAssign';

const { Sider, Content } = Layout;
const { Text } = Typography;

interface MenuItemDef {
  key: string;
  icon: React.ReactNode;
  label: string;
  element: React.ReactNode;
}

const ALL_MENU_ITEMS: MenuItemDef[] = [
  { key: '/', icon: <RocketOutlined />, label: '帖子增长助手', element: <TalkBoost /> },
  { key: '/active-atmosphere', icon: <ThunderboltOutlined />, label: '动态人气直播间', element: <LiveRoomActiveAtmosphere /> },
  { key: '/bot', icon: <RobotOutlined />, label: '机器人管理', element: <BotManagement /> },
  { key: '/bot-avatar-assign', icon: <UserOutlined />, label: '头像昵称分配', element: <BotAvatarAssign /> },
  { key: '/bot-employee', icon: <TeamOutlined />, label: '专属机器人员工', element: <BotEmployee /> },
  { key: '/bot-trtc-group', icon: <TeamOutlined />, label: '机器人群管理', element: <BotGroupManagement /> },
  { key: '/employee-chat', icon: <MessageOutlined />, label: '专属员工聊天', element: <EmployeeChat /> },
  { key: '/post-comment', icon: <CommentOutlined />, label: '帖子评论', element: <PostComment /> },
  { key: '/live-chat', icon: <MessageOutlined />, label: '直播间消息', element: <LiveChat /> },
  { key: '/gift-send', icon: <GiftOutlined />, label: '礼物打赏', element: <GiftSend /> },
  { key: '/polymarket', icon: <SwapOutlined />, label: 'Polymarket 交易', element: <Polymarket /> },
  { key: '/tags', icon: <TagsOutlined />, label: '标签管理', element: <TagManagement /> },
  { key: '/group', icon: <TeamOutlined />, label: '分组管理', element: <GroupManagement /> },
  { key: '/content', icon: <SendOutlined />, label: '内容分发', element: <ContentDistribution /> },
  { key: '/social', icon: <ShareAltOutlined />, label: '社交网络', element: <SocialNetwork /> },
  { key: '/nurture', icon: <RiseOutlined />, label: '账号养成', element: <AccountNurture /> },
  { key: '/accounts', icon: <UserOutlined />, label: '账号管理', element: <AccountManagement /> },
  { key: '/roles', icon: <SafetyOutlined />, label: '角色管理', element: <RoleManagement /> },
  { key: '/operation-logs', icon: <FileTextOutlined />, label: '操作日志', element: <OperationLog /> },
];

const NoPermission: React.FC = () => (
  <Result status="403" title="无权限" subTitle="您没有访问此页面的权限，请联系管理员分配角色。" />
);

const AdminPanel: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { menuKeys, user, logout, token } = useAuth();

  const handleChangePassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      if (!token) return;
      setPwdLoading(true);
      await changePassword(token, values.oldPassword, values.newPassword);
      message.success('密码修改成功');
      setPwdModalOpen(false);
      pwdForm.resetFields();
    } catch (err: any) {
      if (err?.message) message.error(err.message);
    } finally {
      setPwdLoading(false);
    }
  };

  // 是否已分配角色（有 menuKeys）
  const hasRole = menuKeys && menuKeys.length > 0;

  const allowedItems = useMemo(() => {
    if (!hasRole) {
      // 未分配角色：显示全部菜单（向后兼容，如 dev 环境 / Lark 用户未分配角色）
      return ALL_MENU_ITEMS;
    }
    return ALL_MENU_ITEMS.filter(item => menuKeys.includes(item.key));
  }, [menuKeys, hasRole]);

  const allowedKeys = useMemo(() => new Set(allowedItems.map(i => i.key)), [allowedItems]);

  const sidebarItems = useMemo(() =>
    allowedItems.map(({ key, icon, label }) => ({ key, icon, label })),
    [allowedItems]
  );

  const isCurrentAllowed = allowedKeys.has(location.pathname);

  // 懒加载 keep-alive：路径首次访问后加入集合并保持挂载，切换菜单时仅切换 display
  const [mountedKeys, setMountedKeys] = useState<Set<string>>(() =>
    allowedKeys.has(location.pathname) ? new Set([location.pathname]) : new Set()
  );

  useEffect(() => {
    if (!isCurrentAllowed) return;
    setMountedKeys(prev =>
      prev.has(location.pathname) ? prev : new Set(prev).add(location.pathname)
    );
  }, [location.pathname, isCurrentAllowed]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={240}
        collapsedWidth={80}
        style={{
          background: '#ffffff',
          borderRight: '1px solid #f0f0f0',
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0',
        }}>
          {collapsed ? (
            <span style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#722ed1',
            }}>A</span>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#722ed1',
              }}>A</span>
              <span style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.85)',
              }}>Admin Panel</span>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={sidebarItems}
          onClick={({ key }) => navigate(key)}
          style={{
            borderRight: 0,
            padding: '8px 0',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            padding: '16px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(0, 0, 0, 0.45)',
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      </Sider>
      <Layout>
        <div style={{
          height: 48,
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 24px',
          flexShrink: 0,
        }}>
          {user && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'user-info',
                    label: <Text type="secondary">{user.loginType === 'lark' ? 'Lark 登录' : '账号登录'}</Text>,
                    disabled: true,
                  },
                  { type: 'divider' },
                  ...(user.loginType === 'password' ? [{
                    key: 'change-password',
                    icon: <LockOutlined />,
                    label: '修改密码',
                    onClick: () => setPwdModalOpen(true),
                  }, { type: 'divider' as const }] : []),
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: '退出登录',
                    danger: true,
                    onClick: () => { logout(); navigate('/login'); },
                  },
                ],
              }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size="small"
                  src={user.avatar || undefined}
                  icon={!user.avatar ? <UserOutlined /> : undefined}
                  style={{ backgroundColor: user.avatar ? undefined : '#1677ff' }}
                />
                <Text style={{ fontSize: 13 }}>{user.name || user.username || '用户'}</Text>
              </Space>
            </Dropdown>
          )}
        </div>
        <Content
          style={{
            padding: 24,
            background: '#f5f5f5',
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          {allowedItems.map(item => {
            if (!mountedKeys.has(item.key)) return null;
            const isActive = item.key === location.pathname;
            return (
              <div key={item.key} style={{ display: isActive ? 'block' : 'none' }}>
                {item.element}
              </div>
            );
          })}
          {/* 当用户有角色但访问了无权限的路由时，重定向或显示 403 */}
          {hasRole && !isCurrentAllowed && (
            allowedItems.length > 0
              ? <Navigate to={allowedItems[0].key} replace />
              : <NoPermission />
          )}
        </Content>
      </Layout>
      <Modal
        title="修改密码"
        open={pwdModalOpen}
        onOk={handleChangePassword}
        onCancel={() => { setPwdModalOpen(false); pwdForm.resetFields(); }}
        confirmLoading={pwdLoading}
        okText="确认修改"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="oldPassword" label="旧密码" rules={[{ required: true, message: '请输入旧密码' }]}>
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码不能少于6位' },
          ]}>
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminPanel;
